/**
 * Dashboard Service — API client for dashboard-level operations.
 *
 * All dashboard endpoints live under /api/v1/dashboards.
 *
 * Chart-level operations (CRUD, data, AI edit) have been moved to chartsService.ts.
 * This service retains only dashboard-scoped operations:
 * - Dashboard CRUD, duplicate, regenerate
 * - List charts in dashboard
 * - Chart layout (positions)
 * - Filters, sharing
 */
import api from './client';
import type {
  Dashboard,
  DashboardListResponse,
  CreateDashboardRequest,
  CreateDashboardResponse,
  UpdateDashboardRequest,
  UpdateDashboardSettingsRequest,
  Chart,
  ShareInfoResponse,
  ShareUpdateResponse,
  ShareDashboardBody,
  DashboardCreationStatus,
  FilterDataResponse,
  ChartLayoutItem,
  UpdateChartsLayoutResponse,
  ResetChartsLayoutResponse,
} from './types';

export const dashboardsService = {
  // ── Dashboard CRUD ─────────────────────────────────────────────────────

  /**
   * Get user dashboards with pagination
   */
  getUserDashboards: async (
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<DashboardListResponse> => {
    let endpoint = `/dashboards?page=${page}&limit=${limit}`;
    if (search) {
      endpoint += `&search=${encodeURIComponent(search)}`;
    }
    return api.get<DashboardListResponse>(endpoint);
  },

  /**
   * Create a new dashboard asynchronously with AI generation.
   * Returns a jobId that can be used to poll for status.
   */
  createDashboard: async (data: CreateDashboardRequest): Promise<CreateDashboardResponse> => {
    return api.post<CreateDashboardResponse>('/dashboards', data);
  },

  /**
   * Get dashboard by ID (full detail with charts & filters)
   */
  getDashboard: async (id: string): Promise<Dashboard> => {
    return api.get<Dashboard>(`/dashboards/${id}`);
  },

  /**
   * Update dashboard metadata
   */
  updateDashboard: async (id: string, data: UpdateDashboardRequest): Promise<Dashboard> => {
    return api.put<Dashboard>(`/dashboards/${id}`, data);
  },

  /**
   * Partial-update dashboard settings (merge with existing).
   * Debounce on the caller side; this sends immediately.
   */
  updateDashboardSettings: async (id: string, data: UpdateDashboardSettingsRequest): Promise<Dashboard> => {
    return api.patch<Dashboard>(`/dashboards/${id}/settings`, data);
  },

  /**
   * Delete dashboard
   */
  deleteDashboard: async (id: string): Promise<void> => {
    return api.delete<void>(`/dashboards/${id}`);
  },

  /**
   * Duplicate an existing dashboard
   */
  duplicateDashboard: async (dashboardId: string): Promise<Dashboard> => {
    return api.post<Dashboard>(`/dashboards/${dashboardId}/duplicate`, {});
  },

  // ── Charts in dashboard (list, layout) ─────────────────────────────────

  /**
   * Get all charts in a dashboard
   */
  getDashboardCharts: async (dashboardId: string): Promise<Chart[]> => {
    return api.get<Chart[]>(`/dashboards/${dashboardId}/charts`);
  },

  /**
   * Batch-update chart positions (autosave from drag/resize)
   */
  updateChartsLayout: async (
    dashboardId: string,
    layouts: ChartLayoutItem[]
  ): Promise<UpdateChartsLayoutResponse> => {
    return api.put<UpdateChartsLayoutResponse>(
      `/dashboards/${dashboardId}/charts/layout`,
      { layouts }
    );
  },

  /**
   * Reset all chart positions to auto-arrange
   */
  resetChartsLayout: async (dashboardId: string): Promise<ResetChartsLayoutResponse> => {
    return api.post<ResetChartsLayoutResponse>(
      `/dashboards/${dashboardId}/charts/reset-layout`,
      {}
    );
  },

  // ── Jobs ────────────────────────────────────────────────────────────────

  /**
   * Get dashboard creation job status (for AI-generated dashboards)
   */
  getJobStatus: async (jobId: string): Promise<DashboardCreationStatus> => {
    return api.get<DashboardCreationStatus>(`/dashboards/jobs/${jobId}/status`);
  },

  cancelJob: async (jobId: string): Promise<{ jobId: string; status: string }> => {
    return api.post<{ jobId: string; status: string }>(`/dashboards/jobs/${jobId}/cancel`);
  },

  /**
   * Regenerate a canceled or failed dashboard.
   */
  regenerate: async (dashboardId: string): Promise<CreateDashboardResponse> => {
    return api.post<CreateDashboardResponse>(`/dashboards/${dashboardId}/regenerate`);
  },

  // ── Sharing ─────────────────────────────────────────────────────────────

  /**
   * Get dashboard sharing information
   */
  getShareInfo: async (dashboardId: string): Promise<ShareInfoResponse> => {
    return api.get<ShareInfoResponse>(`/dashboards/${dashboardId}/share`);
  },

  /**
   * Share dashboard with users or make public
   */
  shareDashboard: async (dashboardId: string, data: ShareDashboardBody): Promise<ShareUpdateResponse> => {
    return api.post<ShareUpdateResponse>(`/dashboards/${dashboardId}/share`, data);
  },

  /**
   * Replace the current link and invalidate all existing links and embeds.
   */
  resetShareLink: async (dashboardId: string): Promise<ShareUpdateResponse> => {
    return api.post<ShareUpdateResponse>(`/dashboards/${dashboardId}/share/reset`, {});
  },

  // ── Filters ─────────────────────────────────────────────────────────────

  /**
   * Get filter data (options for categorical filters).
   * Supports server-side search and pagination for high-cardinality filters.
   */
  getFilterData: async (
    dashboardId: string,
    filterId: string,
    options?: { search?: string; limit?: number; offset?: number }
  ): Promise<FilterDataResponse> => {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return api.get<FilterDataResponse>(
      `/dashboards/${dashboardId}/filters/${filterId}/data${qs ? `?${qs}` : ''}`
    );
  },

  // ── Thumbnails ───────────────────────────────────────────────────────────────

  /**
   * Upload a dashboard thumbnail as a base64 data URL
   */
  uploadThumbnail: async (dashboardId: string, dataUrl: string, darkDataUrl?: string): Promise<{ dashboardId: string }> => {
    return api.put<{ dashboardId: string }>(`/dashboards/${dashboardId}/thumbnail`, { dataUrl, darkDataUrl });
  },
};

export default dashboardsService;
